import { TimeZoneType } from "../../../constants/timezones"
import { DateTimeService } from "../../../services/date-time-service"
import {
  getCustomerOptions,
  getMembershipOptions,
  getOptionId,
  getOptionName,
  getSupplierOptions,
  getWorkspaceOptions,
} from "../../../options/utils"
import {
  getColorHtmlForColor,
  getStateIconHtml,
  makeEnumKeysFromStatusOptions,
  makeEnumString,
  mapColorToTranslations,
  mapStatusToTranslations,
} from "../../utils"
import { ProjectActivityRow } from "../types"
import { Option } from "../../types"
import { sortBy } from "lodash"
import { isProjectPlannedEndDatePastDue, isProjectPlannedStartDatePastDue } from "../../../projects/utils/date-utils"
import { ProjectViewModel } from "../../../projects/api/project"
import { TreeGridTranslations } from "../../hooks/use-translations"
import { getGanttGanttHtmlRight } from "../../utils/tree-grid"
import { Status } from "../../../types/common"

type MakeProjectActivityRowProps = {
  project: ProjectViewModel
  dateFormat: string
  timeZone: TimeZoneType
  options: Option[]
  translations: TreeGridTranslations
}

const getPlannedStartDate = (project: ProjectViewModel, dateTimeService: DateTimeService) => {
  return project.plannedStartDate ? dateTimeService.removeTimezoneOffset(project.plannedStartDate, "UTC").getTime() : ""
}

const getPlannedEndDate = (project: ProjectViewModel, dateTimeService: DateTimeService) => {
  return project.plannedEndDate ? dateTimeService.removeTimezoneOffset(project.plannedEndDate, "UTC").getTime() : ""
}

const getActualStartDate = (project: ProjectViewModel, dateTimeService: DateTimeService) => {
  return project.actualStartDate ? dateTimeService.removeTimezoneOffset(project.actualStartDate, "UTC").getTime() : ""
}

const getActualBarStartDate = (project: ProjectViewModel, dateTimeService: DateTimeService) => {
  return getActualStartDate(project, dateTimeService)
}

const getActualBarEndDate = (project: ProjectViewModel, dateTimeService: DateTimeService) => {
  const now = new Date().toISOString()
  return project.isInProgress ? dateTimeService.removeTimezoneOffset(now, "UTC").getTime() : ""
}

const getPlannedStartDateClass = (project: ProjectViewModel, dateTimeService: DateTimeService) => {
  const isPastDue = isProjectPlannedStartDatePastDue({ project, dateTimeService })
  return isPastDue ? "redText" : ""
}

const getPlannedEndDateClass = (project: ProjectViewModel, dateTimeService: DateTimeService) => {
  const isPastDue = isProjectPlannedEndDatePastDue({ project, dateTimeService })
  return isPastDue ? "redText" : ""
}

function getStatusDescriptionTip(project: ProjectViewModel, dateTimeService: DateTimeService, dateFormat: string) {
  const sortedUpdates = sortBy(project.statusDescriptionUpdates, "updatedAt")
  const latestUpdate = sortedUpdates[sortedUpdates.length - 1]
  let tip = ""
  if (latestUpdate) {
    const formatStr = `${dateFormat} HH:mm`
    const { updatedBy, updatedAt } = latestUpdate
    const offsetDate = dateTimeService.removeTimezoneOffset(updatedAt)
    tip += updatedBy
    tip += "&nbsp;&nbsp;&bull;&nbsp;&nbsp;"
    tip += dateTimeService.format(offsetDate, formatStr)
  }
  return tip
}

const getStatusEnum = (project: ProjectViewModel, translations: TreeGridTranslations) => {
  return makeEnumString(
    project.statusOptions.map(({ label }) => {
      return mapStatusToTranslations(translations)[label as Status] || label
    })
  )
}

const makeFilterOptionByOrganisation = (project: ProjectViewModel) => {
  return function (option: Option) {
    return option.orgId === project.maintainerId
  }
}

export const makeProjectActivityRow = ({
  project,
  dateFormat,
  timeZone,
  options,
  translations,
}: MakeProjectActivityRowProps): ProjectActivityRow => {
  const filterOptionByOrganisation = makeFilterOptionByOrganisation(project)
  const { id, title, description, statusDescription, enableTimeComponent, plannedStartDate, plannedEndDate } = project
  const dateTimeService = new DateTimeService({ dateFormat, timeZone, enableTimeComponent })
  const dateTimeFormat = dateTimeService.getFormat()
  const status = mapStatusToTranslations(translations)[project.customStatus as Status] || project.customStatus
  const statusEnum = getStatusEnum(project, translations)
  const statusEnumKeys = makeEnumKeysFromStatusOptions(project.statusOptions)

  const ganttGanttHtmlRight = getGanttGanttHtmlRight({
    title: project.title,
    startDate: plannedStartDate,
    endDate: plannedEndDate,
    dateTimeService,
    showTime: enableTimeComponent,
  })
  const GanttGanttHtmlRight = "*ganttGanttHtmlRight*"
  const translatedColor = mapColorToTranslations(translations)[project.ganttBarColor]
  const ganttBarColor = getColorHtmlForColor({ color: project.ganttBarColor, translatedColor })
  const GanttGanttClass = project.ganttBarColor === "Default" ? "Blue" : project.ganttBarColor

  const customers = project.customers.map((c) => c.id).join(";")
  const customerOptions = getCustomerOptions(options).filter(filterOptionByOrganisation)
  const customerNames = customerOptions.map(getOptionName)
  const customerIds = customerOptions.map(getOptionId)
  const deactivatedCustomers = project.customers.filter(({ id }) => !customerIds.includes(id))
  const deactivatedCustomersNames = deactivatedCustomers.map(({ name }) => name)
  const deactivatedCustomersIds = deactivatedCustomers.map(({ id }) => id)
  const customersEnum = makeEnumString([...customerNames, ...deactivatedCustomersNames])
  const customersEnumKeys = makeEnumString([...customerIds, ...deactivatedCustomersIds])

  const responsible = project.managers.map((r) => r.id).join(";")
  const membershipOptions = getMembershipOptions(options).filter(filterOptionByOrganisation)
  const responsibleNames = membershipOptions.map(getOptionName)
  const responsibleIds = membershipOptions.map(getOptionId)
  const deactivatedResponsible = project.managers.filter(({ id }) => !responsibleIds.includes(id))
  const deactivatedResponsibleNames = deactivatedResponsible.map(({ name }) => name)
  const deactivatedResponsibleIds = deactivatedResponsible.map(({ id }) => id)
  const responsibleEnum = makeEnumString([...responsibleNames, ...deactivatedResponsibleNames])
  const responsibleEnumKeys = makeEnumString([...responsibleIds, ...deactivatedResponsibleIds])

  const participants = project.participants.map((r) => r.id).join(";")
  const participantOptions = membershipOptions.filter(({ id }) => !responsible.includes(id))
  const participantNames = participantOptions.map(getOptionName)
  const participantIds = participantOptions.map(getOptionId)
  const deactivatedParticipants = project.participants.filter(({ id }) => !participantIds.includes(id))
  const deactivatedParticipantsNames = deactivatedParticipants.map(({ name }) => name)
  const deactivatedParticipantsIds = deactivatedParticipants.map(({ id }) => id)
  const participantsEnum = makeEnumString([...participantNames, ...deactivatedParticipantsNames])
  const participantsEnumKeys = makeEnumString([...participantIds, ...deactivatedParticipantsIds])

  const suppliers = project.suppliers.map((s) => s.id).join(";")
  const supplierOptions = getSupplierOptions(options).filter(filterOptionByOrganisation)
  const supplierNames = supplierOptions.map(getOptionName)
  const supplierIds = supplierOptions.map(getOptionId)
  const deactivatedSuppliers = project.suppliers.filter(({ id }) => !supplierIds.includes(id))
  const deactivatedSuppliersNames = deactivatedSuppliers.map(({ name }) => name)
  const deactivatedSuppliersIds = deactivatedSuppliers.map(({ id }) => id)
  const suppliersEnum = makeEnumString([...supplierNames, ...deactivatedSuppliersNames])
  const suppliersEnumKeys = makeEnumString([...supplierIds, ...deactivatedSuppliersIds])

  const workspaces = project.workspaces.map((w) => w.id).join(";")
  const workspaceOptions = getWorkspaceOptions(options).filter(filterOptionByOrganisation)
  const workspaceNames = workspaceOptions.map(getOptionName)
  const workspaceIds = workspaceOptions.map(getOptionId)
  const deactivatedWorkspaces = project.workspaces.filter(({ id }) => !workspaceIds.includes(id))
  const deactivatedWorkspacesNames = deactivatedWorkspaces.map(({ name }) => name)
  const deactivatedWorkspacesIds = deactivatedWorkspaces.map(({ id }) => id)
  const workspacesEnum = makeEnumString([...workspaceNames, ...deactivatedWorkspacesNames])
  const workspacesEnumKeys = makeEnumString([...workspaceIds, ...deactivatedWorkspacesIds])

  const linksHtml = project.links.map((link) => link.anchorTag)
  let filesHtml: string[] = []
  if (project.canViewProjectAttachments && project.files.length > 0) {
    filesHtml = project.files.map(
      (file) => `<a href="#" data-file-id="${file.id}" onclick="return false;">${file.filename}</a>`
    )
  }
  const attachmentsHtml = [...linksHtml, ...filesHtml].join(", ")

  return {
    id,
    type: "project",
    open: "/external-link-icon.svg",
    activityNumber: project.customProjectNumber || project.projectNumber,
    title,
    description,
    status,
    state: getStateIconHtml(project.state),
    statusDescription,
    statusDescriptionTip: getStatusDescriptionTip(project, dateTimeService, dateFormat),
    enableTimeComponent,
    plannedStartDate: getPlannedStartDate(project, dateTimeService),
    plannedStartDateClass: getPlannedStartDateClass(project, dateTimeService),
    plannedStartDateFormat: dateTimeFormat,
    plannedEndDate: getPlannedEndDate(project, dateTimeService),
    plannedEndDateClass: getPlannedEndDateClass(project, dateTimeService),
    plannedEndDateFormat: dateTimeFormat,
    actualStartDate: getActualStartDate(project, dateTimeService),
    actualStartDateFormat: dateTimeFormat,
    actualBarStartDate: getActualBarStartDate(project, dateTimeService),
    actualBarEndDate: getActualBarEndDate(project, dateTimeService),
    linksAndAttachments: attachmentsHtml,
    completionPercentage: project.completionPercentage,
    completionPercentageHtmlPostfix: "%",
    completionPercentageBasedOnTasks: project.completionPercentageBasedOnTasks,
    completionPercentageBasedOnTasksHtmlPostfix: "%",
    responsible,
    responsibleEnum,
    responsibleEnumKeys,
    participants,
    participantsEnum,
    participantsEnumKeys,
    customers,
    customersEnum,
    customersEnumKeys,
    suppliers,
    suppliersEnum,
    suppliersEnumKeys,
    workspaces,
    workspacesEnum,
    workspacesEnumKeys,
    GanttGanttHtmlRight,
    ganttGanttHtmlRight,
    GanttGanttClass,
    ganttBarColor,
    ganttBarColorFilterValue: translatedColor,
    activityNumberCanEdit: project.canUpdateDetails ? 1 : 0,
    titleCanEdit: project.canUpdateDetails ? 1 : 0,
    descriptionCanEdit: project.canUpdateDetails ? 1 : 0,
    ganttBarColorCanEdit: project.canUpdateDetails ? 1 : 0,
    plannedStartDateCanEdit: project.canUpdatePlan ? 1 : 0,
    plannedEndDateCanEdit: project.canUpdatePlan ? 1 : 0,
    statusCanEdit: project.canUpdateStatus ? 1 : 0,
    stateCanEdit: project.canUpdateStatus ? 1 : 0,
    statusDescriptionCanEdit: project.canUpdateStatus ? 1 : 0,
    actualStartDateCanEdit: project.canUpdateStatus ? 1 : 0,
    customersCanEdit: project.canUpdateCustomers && project.isOrgProject ? 1 : 0,
    responsibleCanEdit: project.canUpdateManagers && project.isOrgProject ? 1 : 0,
    participantsCanEdit: project.canUpdateParticipants && project.isOrgProject ? 1 : 0,
    suppliersCanEdit: project.canUpdateSuppliers && project.isOrgProject ? 1 : 0,
    workspacesCanEdit: project.canUpdateWorkspaces && project.isOrgProject ? 1 : 0,
    completionPercentageCanEdit: project.canUpdateStatus ? 1 : 0,
    completionPercentageBasedOnTasksCanEdit: 0,
    Height: 48,
    MaxHeight: 48,
    statusEnum,
    statusEnumKeys,
  }
}
